import { useCallback, useContext, useEffect, useState } from "react";
import { CoreContext } from "@scmlatam/web-ui";
import { permissionEventsService } from "../../services/permissionEvents";
import { refreshSession } from "../../services/instances";
import { signServices } from "../../services/sign";

const MAX_BACKOFF_MS = 30_000;
const TOKEN_CHANGE_POLL_MS = 5_000;

const wait = (ms: number, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, ms);
        signal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                resolve();
            },
            { once: true },
        );
    });

const usePermissionEvents = () => {
    const { isAuthenticated, setIsAuthenticated } = useContext(CoreContext);
    const [permissionsChanged, setPermissionsChanged] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) return;

        const controller = new AbortController();
        const { signal } = controller;

        const run = async () => {
            let attempt = 0;

            while (!signal.aborted) {
                const token = localStorage.getItem("access_token");
                if (!token) return;

                try {
                    const { status } = await permissionEventsService.streamPermissionEvents(
                        token,
                        signal,
                        (event) => {
                            if (event.type === "connected") attempt = 0;
                            if (event.type === "permissions_changed") setPermissionsChanged(true);
                        },
                    );

                    if (status === 401 || status === 403) {
                        // No se refresca aquí para no extender sesiones inactivas; se espera a que el
                        // interceptor de axios renueve el token por actividad del usuario.
                        while (!signal.aborted && localStorage.getItem("access_token") === token) {
                            await wait(TOKEN_CHANGE_POLL_MS, signal);
                        }
                        continue;
                    }
                } catch {
                    if (signal.aborted) return;
                }

                attempt += 1;
                await wait(Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt), signal);
            }
        };

        void run();
        return () => controller.abort();
    }, [isAuthenticated]);

    const applyChanges = useCallback(async () => {
        setRefreshing(true);
        const newToken = await refreshSession();

        if (!newToken) {
            signServices.logout(setIsAuthenticated);
            window.location.assign("/signin");
            return;
        }

        signServices.syncSessionFromToken(newToken);
        window.location.reload();
    }, [setIsAuthenticated]);

    const dismiss = useCallback(() => setPermissionsChanged(false), []);

    return { permissionsChanged, refreshing, applyChanges, dismiss };
};

export { usePermissionEvents };
